"use client";

import { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Chip,
  Tooltip,
} from "@mui/material";
import {
  Search as SearchIcon,
  Person as PersonIcon,
  LocalHospital as LocalHospitalIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  Event as EventIcon,
  AttachMoney as AttachMoneyIcon,
  SupervisorAccount as SupervisorAccountIcon,
} from "@mui/icons-material";
import formatDateTime from "../../../Components/GeneralComponents/DateFormatter/FormatDateTime";
import useFetchObjects from "../../../api/useFetchObjects";
import { useAuth } from "../../../context";
import ErrorPage from "../../../Components/GeneralComponents/errorPage/ErrorPage";
import FormatDateTime from "../../../Components/GeneralComponents/DateFormatter/FormatDateTime";

// Status color mapping
const statusColors = {
  14: "#ffebee",
  13: "#ffccbc",
  12: "#e8f5e9",
  11: "#fff9c4",
  10: "#f3e5f5",
  9: "#e8eaf6",
  8: "#fce4ec",
  7: "#e1f5fe",
  6: "#f1f8e9",
  5: "#e0f2f1",
  4: "#ffecb3",
  3: "#dcedc8",
  2: "#e0f7fa",
  1: "#d1c4e9",
};

const Loading = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      p: 4,
    }}
  >
    <Typography
      variant="h6"
      color="text.secondary"
      sx={{ display: "flex", alignItems: "center" }}
    >
      <Box
        sx={{
          width: 24,
          height: 24,
          mr: 1,
          borderRadius: "50%",
          border: "3px solid #f0f0f0",
          borderTop: "3px solid #3498db",
          animation: "spin 1s linear infinite",
          "@keyframes spin": {
            "0%": { transform: "rotate(0deg)" },
            "100%": { transform: "rotate(360deg)" },
          },
        }}
      />
      Loading...
    </Typography>
  </Box>
);

const normalizeDate = (dateStr) => {
  try {
    if (!dateStr) return "";
    // If dateStr is exactly YYYY-MM-DD, append T00:00:00 for ISO format
    if (typeof dateStr === "string" && dateStr.length === 10) {
      return dateStr + "T00:00:00";
    }
    return dateStr;
  } catch {
    return "";
  }
};

const Search = () => {
  const auth = useAuth();
  const token = auth?.user?.token;

  const [inputValue, setInputValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  // Handle search input without debounce to prevent typing issues
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
  };

  // Trigger the search with a slight delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(inputValue);
    }, 1000);

    return () => clearTimeout(timer);
  }, [inputValue]);

  // API call using custom hook
  const {
    data: combinedResults = [],
    isLoading,
    isError,
    refetch,
  } = useFetchObjects(
    ["search", searchTerm],
    `search/?number=${encodeURIComponent(searchTerm)}`,
    token
  );

  // Refetch when search term changes
  useEffect(() => {
    if (searchTerm) {
      setIsSearching(true);
      refetch().finally(() => setIsSearching(false));
    }
  }, [searchTerm, refetch]);

  const getStatusColor = (statusId) => {
    return statusColors[statusId] || "#ffffff";
  };

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Paper
        elevation={3}
        sx={{
          p: 3,
          borderRadius: 2,
          background: "linear-gradient(to right, #ffffff, #f9f9f9)",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            mb: 3,
            textAlign: "center",
            fontWeight: 600,
            color: "primary.main",
          }}
        >
          Search Clients & Patients
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 4,
          }}
        >
          <TextField
            type="search"
            size="medium"
            value={inputValue}
            onChange={handleSearchChange}
            disabled={isSearching}
            sx={{
              width: { xs: "100%", sm: "70%", md: "50%" },
              "& .MuiOutlinedInput-root": {
                borderRadius: "28px",
                bgcolor: "#f5f5f5",
                "&:hover": {
                  bgcolor: "#f0f0f0",
                },
                "&.Mui-focused": {
                  bgcolor: "#ffffff",
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="primary" />
                </InputAdornment>
              ),
            }}
            placeholder="Search by name, phone, or ID"
            variant="outlined"
          />
        </Box>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <Loading />
          </Box>
        ) : isError ? (
          <ErrorPage />
        ) : combinedResults.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              p: 4,
              bgcolor: "#f5f5f5",
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" color="text.secondary">
              No results found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Try a different search term
            </Typography>
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              borderRadius: 2,
              overflow: "auto",
              border: "1px solid #e0e0e0",
            }}
          >
            <Table sx={{ minWidth: 1100 }}>
              <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                    Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                    Contact
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                    Address
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                    Procedure/Status
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                    Date
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                    Follow Up By
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                    Additional Info
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                    Registration Date
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                    Type
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {combinedResults.map((record, index) => {
                  const name =
                    record.record_type === "client"
                      ? record.full_name
                      : `${record.firstname || ""} ${
                          record.lastname || ""
                        }`.trim();
                  const contact =
                    record.record_type === "client"
                      ? record.contact || "N/A"
                      : record.phone || "N/A";
                  const address = record.address || "N/A";

                  const procedureStatus =
                    record.record_type === "client"
                      ? record?.status_info?.name || record.status || "N/A"
                      : record.blood_type || "N/A";

                  const date =
                    record.record_type === "client"
                      ? formatDateTime(record.procedure_date)
                      : "N/A";

                  const followUpBy = record.follow_up_by || "N/A";

                  const additionalInfo =
                    record.record_type === "client"
                      ? `Age: ${record.age ?? "N/A"}, Gender: ${
                          record.gender ?? "N/A"
                        }`
                      : `${
                          record.passport ? `Passport: ${record.passport}` : ""
                        }${
                          record.dosia_file_id
                            ? `, File ID: ${record.dosia_file_id}`
                            : ""
                        }` || "N/A";

                  const rowBgColor =
                    record.record_type === "client" && record.status
                      ? getStatusColor(record.status)
                      : "#ffffff";

                  return (
                    <TableRow
                      key={index}
                      sx={{
                        backgroundColor: rowBgColor,
                        "&:hover": { bgcolor: "#f0f7ff" },
                        transition: "background-color 0.2s",
                      }}
                    >
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {name || "N/A"}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Tooltip title="Click to call" arrow>
                          <Typography
                            component="a"
                            href={`tel:${contact}`}
                            sx={{
                              textDecoration: "none",
                              color: "primary.main",
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            {contact}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={address} arrow>
                          <Typography
                            sx={{
                              maxWidth: 200,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {address}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {record.record_type === "client" ? (
                          <>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {record?.display_procedure?.name || "N/A"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Status: {procedureStatus}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2">
                            Blood Type: {procedureStatus}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {date}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {followUpBy !== "N/A" ? (
                          <Chip
                            size="small"
                            label={followUpBy}
                            color="info"
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                          />
                        ) : (
                          followUpBy
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={additionalInfo} arrow>
                          <Typography
                            sx={{
                              maxWidth: 200,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {additionalInfo}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {FormatDateTime(
                          record?.created_at && record.created_at.length === 10
                            ? record.created_at + "T00:00:00"
                            : record.created_at
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={record.record_type}
                          color={
                            record.record_type === "client"
                              ? "primary"
                              : "secondary"
                          }
                          variant="outlined"
                          icon={
                            record.record_type === "client" ? (
                              <PersonIcon fontSize="small" />
                            ) : (
                              <LocalHospitalIcon fontSize="small" />
                            )
                          }
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default Search;
